import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HistoryIcon from "@mui/icons-material/History";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProjectAssignmentsApi from "../../../../../infrastructure/api/projectAssignments";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import ContractorSupervisionWorkspaceShell from "../ContractorSupervisionWorkspaceShell";
import { CONTRACTOR_SUPERVISION_NAV_ITEMS } from "../contractorSupervisionNav";

const CARD_SX = {
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
};

const PATHS = {
  projects: CONTRACTOR_SUPERVISION_NAV_ITEMS.find((x) => x.id === "projects")
    .path,
  closedProjects: CONTRACTOR_SUPERVISION_NAV_ITEMS.find(
    (x) => x.id === "closedProjects"
  ).path,
};

const formatFa = (value) => Number(value || 0).toLocaleString("fa-IR");

const resolveStatus = (status) => {
  if (status == null || status === "") return null;
  if (typeof status === "number") return status;
  const map = {
    Assigned: 1,
    Pending: 1,
    Accepted: 2,
    Rejected: 3,
    Canceled: 4,
    Completed: 5,
    Superseded: 6,
  };
  if (map[status] != null) return map[status];
  const n = Number(status);
  return Number.isNaN(n) ? null : n;
};

const hasValidVisitDate = (value) => {
  if (!value) return false;
  if (typeof value === "string" && value.startsWith("0001-01-01")) return false;
  if (value instanceof Date && value.getFullYear() <= 1) return false;
  return true;
};

/**
 * داشبورد نظارت پیمانکار — همان UX داشبورد ناظر،
 * با داده Area=Contractor (بدون تماس به Supervisor).
 */
const ContractorSupervisionHubPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    pendingAssignments: 0,
    acceptedAssignments: 0,
    closedAssignments: 0,
    pendingVisitScheduling: 0,
    scheduledButNotCompleted: 0,
    completedSiteVisits: 0,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      const next = {
        pendingAssignments: 0,
        acceptedAssignments: 0,
        closedAssignments: 0,
        pendingVisitScheduling: 0,
        scheduledButNotCompleted: 0,
        completedSiteVisits: 0,
      };

      try {
        const assignmentsRes = await ProjectAssignmentsApi.getMyAssignments(
          "Contractor"
        );

        if (assignmentsRes?.ok && Array.isArray(assignmentsRes.data)) {
          const list = assignmentsRes.data;
          next.pendingAssignments = list.filter(
            (x) => resolveStatus(x.projectAssignmentStatus) === 1
          ).length;
          next.acceptedAssignments = list.filter((x) => {
            const status = resolveStatus(x.projectAssignmentStatus);
            if (status !== 2) return false;
            const actual =
              x.relatedSiteVisitActualVisitTime ??
              x.RelatedSiteVisitActualVisitTime;
            return !hasValidVisitDate(actual);
          }).length;
          next.closedAssignments = list.filter((x) => {
            const status = resolveStatus(x.projectAssignmentStatus);
            if ([3, 4, 5, 6].includes(status)) return true;
            if (status === 2) {
              const actual =
                x.relatedSiteVisitActualVisitTime ??
                x.RelatedSiteVisitActualVisitTime;
              return hasValidVisitDate(actual);
            }
            return false;
          }).length;

          // Visit KPIs derived from contractor assignments (no Supervisor API).
          list.forEach((x) => {
            const status = resolveStatus(x.projectAssignmentStatus);
            const hasSiteVisit = !!(
              x.relatedSiteVisitId ?? x.RelatedSiteVisitId
            );
            if (!hasSiteVisit) return;
            const scheduled =
              x.relatedSiteVisitScheduledVisitTime ??
              x.RelatedSiteVisitScheduledVisitTime;
            const actual =
              x.relatedSiteVisitActualVisitTime ??
              x.RelatedSiteVisitActualVisitTime;
            const isScheduled = hasValidVisitDate(scheduled);
            const isCompleted = hasValidVisitDate(actual);

            if (isCompleted || status === 5) {
              next.completedSiteVisits += 1;
            } else if (isScheduled) {
              next.scheduledButNotCompleted += 1;
            } else if (status === 2) {
              next.pendingVisitScheduling += 1;
            }
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError("بارگذاری داشبورد با خطا مواجه شد. دوباره تلاش کنید.");
        }
      }

      if (!cancelled) {
        setStats(next);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const kpiCards = useMemo(
    () => [
      {
        title: "ارجاع در انتظار قبول",
        value: stats.pendingAssignments,
        sub: "نیاز به اقدام در پروژه‌های جاری",
        color: "#ed6c02",
        icon: <PendingActionsIcon />,
        to: PATHS.projects,
      },
      {
        title: "پروژه‌های پذیرفته‌شده",
        value: stats.acceptedAssignments,
        sub: "ارجاع فعال شما",
        color: "#2e7d32",
        icon: <WorkOutlineIcon />,
        to: PATHS.projects,
      },
      {
        title: "پروژه‌های قبلی",
        value: stats.closedAssignments,
        sub: "تکمیل‌شده یا بسته‌شده",
        color: "#546e7a",
        icon: <HistoryIcon />,
        to: PATHS.closedProjects,
      },
      {
        title: "منتظر تعیین زمان بازدید",
        value: stats.pendingVisitScheduling,
        sub: "از پروژه‌های جاری پیگیری کنید",
        color: "#ff9800",
        icon: <ScheduleIcon />,
        to: PATHS.projects,
      },
      {
        title: "در صف بازدید",
        value: stats.scheduledButNotCompleted,
        sub: "زمان ثبت شده، بازدید انجام نشده",
        color: "#5c6bc0",
        icon: <EventAvailableIcon />,
        to: PATHS.projects,
      },
      {
        title: "بازدیدهای تکمیل‌شده",
        value: stats.completedSiteVisits,
        sub: "بازدیدهای انجام‌شده",
        color: "#43a047",
        icon: <CheckCircleIcon />,
        to: PATHS.closedProjects,
      },
    ],
    [stats]
  );

  const nextActions = [
    {
      key: "pending-assign",
      title: "ارجاع‌های جدید",
      description:
        stats.pendingAssignments > 0
          ? `${formatFa(stats.pendingAssignments)} ارجاع در انتظار قبول دارید.`
          : "ارجاع جدیدی در انتظار قبول نیست.",
      button: "پروژه‌های جاری",
      to: PATHS.projects,
      severity: stats.pendingAssignments > 0 ? "warning" : "info",
      icon: <AssignmentIcon />,
    },
    {
      key: "closed",
      title: "پروژه‌های قبلی",
      description:
        stats.closedAssignments > 0
          ? `${formatFa(stats.closedAssignments)} پروژه قبلی یا بسته‌شده.`
          : "پروژه قبلی ثبت نشده است.",
      button: "مشاهده پروژه‌های قبلی",
      to: PATHS.closedProjects,
      severity: "info",
      icon: <HistoryIcon />,
    },
  ];

  return (
    <ContractorSupervisionWorkspaceShell
      title="داشبورد نظارت"
      actions={
        <>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(PATHS.projects)}
            sx={{ fontWeight: 700 }}
          >
            پروژه‌های جاری
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(PATHS.closedProjects)}
            sx={{ fontWeight: 700 }}
          >
            پروژه‌های قبلی
          </Button>
        </>
      }
    >
      <BreadcrumbsComponent />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            {kpiCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <Card
                  sx={{
                    ...CARD_SX,
                    height: "100%",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: 3 },
                  }}
                  onClick={() => navigate(card.to)}
                >
                  <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Avatar sx={{ bgcolor: card.color, width: 36, height: 36 }}>
                        {card.icon}
                      </Avatar>
                      <Typography fontWeight={700} fontSize={{ xs: 13, md: 14 }}>
                        {card.title}
                      </Typography>
                    </Stack>
                    <Typography
                      fontWeight={800}
                      fontSize={{ xs: 22, md: 24 }}
                      color={card.color}
                    >
                      {formatFa(card.value)}
                    </Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {card.sub}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={1.5}>
            {nextActions.map((action) => (
              <Grid item xs={12} md={6} key={action.key}>
                <Card sx={{ ...CARD_SX, height: "100%" }}>
                  <CardContent>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{ bgcolor: "#005B8F", width: 36, height: 36 }}
                        >
                          {action.icon}
                        </Avatar>
                        <Typography fontWeight={800}>{action.title}</Typography>
                        <Chip
                          size="small"
                          label={
                            action.severity === "warning"
                              ? "نیازمند اقدام"
                              : "آماده"
                          }
                          color={
                            action.severity === "warning" ? "warning" : "default"
                          }
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                      <Button
                        variant={
                          action.severity === "warning" ? "contained" : "outlined"
                        }
                        onClick={() => navigate(action.to)}
                        sx={{
                          alignSelf: { xs: "stretch", sm: "flex-start" },
                          fontWeight: 700,
                        }}
                      >
                        {action.button}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Alert severity="info" sx={{ borderRadius: 2 }}>
            از نوار بالا بین داشبورد، پروژه‌های جاری و پروژه‌های قبلی جابه‌جا
            شوید. این کارتابل مخصوص پیمانکار مجری است و با پنل ناظر سولارنت
            جداست.
          </Alert>
        </Stack>
      )}
    </ContractorSupervisionWorkspaceShell>
  );
};

export default ContractorSupervisionHubPage;
